/*------------------------------------------------------------------------------
   About      : Null-safe holder of the user's granted permissions (RBAC v2)

   Populated during session init alongside the legacy PermissionsManager and
   consumed by the usePermissions hook. Unset state degrades to deny-by-default
   (empty grants) instead of throwing.

   Holds two independent grant sets, one per backend authority:
   - Strapi action strings (`permissions`), from the Strapi user record.
   - proxypulse BFF feature endpoints (`bffFeatures`), from proxypulse's own
     feature-based RBAC (`GET /rbac/users/:id/permissions/tree`). Different
     id-space, fetched separately — see SessionManager.requestBffFeatures().

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { UserRoleInfo } from 'types/apis/sessionInterfaces'

export class AccessInfo {

  private userRoleInfo   ?: UserRoleInfo
  private bffFeatures      : readonly string[] = []
  private superAdmin       : boolean = false

  setUserRoleInfo(userRoleInfo : UserRoleInfo) {
    this.userRoleInfo = userRoleInfo
  }

  getGrantedActions() : readonly string[] {
    return this.userRoleInfo?.permissions ?? []
  }

  getRoleName() : string | undefined {
    return this.userRoleInfo?.role?.name
  }

  setBffFeatures(endpoints : readonly string[]) {
    this.bffFeatures = endpoints
  }

  getGrantedBffFeatures() : readonly string[] {
    return this.bffFeatures
  }

  /**
   * True when proxypulse's own `_is_super_admin()` check (any attached role
   * flagged `is_super_admin`, or the env-list rollout fallback) says this user
   * bypasses feature checks server-side. Set from the permission-tree fetch —
   * see SessionManager.requestBffFeatures. Deny-by-default (false) if unset or
   * the fetch failed, matching every other grant on this class.
   */
  setSuperAdmin(superAdmin : boolean) {
    this.superAdmin = superAdmin
  }

  getIsSuperAdmin() : boolean {
    return this.superAdmin
  }
}
